import React, { useCallback, useRef, useEffect, useState } from 'react';

import { useParams, useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Box, Slide, Divider, Modal } from '@material-ui/core';

import { HiOutlinePencil } from 'react-icons/hi';
import { MdWork, MdEmail, MdLock } from 'react-icons/md';
import { IoIosWarning, IoIosArrowBack } from 'react-icons/io';
import { BiX } from 'react-icons/bi';
import * as S from './styles';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import Select from '../../newComponents/UnformSelect';
import Button from '../../newComponents/Button';
import Toggle from '../../newComponents/Toggle';

interface EditUserRequest {
  nome: string;
  cargo: string;
  email: string;
  tipo_acesso: string;
  senha: string;
}

interface Option {
  id: string;
  value: string;
  label: string;
}

const EditFuncionarioV2: React.FC = () => {
  const { id } = useParams<any>();

  const history = useHistory();
  const { rotinas, findRotinas } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<Option[]>([]);
  const [modalDeleteIsOpen, setModalDeleteIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState({
    id: '',
    value: '',
    label: '',
  });
  const [funcionario, setFuncionario] = useState({
    nome: '',
    cargo: '',
    email: '',
    senha: '',
  });
  const [acessoPdv, setAcessoPdv] = useState(true);

  const hasPermission = useCallback(
    (): boolean => findRotinas('funcionarios')?.alterar || false,
    [findRotinas],
  );

  const handleEditFuncionario = useCallback(
    async (data: EditUserRequest) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape(
          {
            email: Yup.string()
              .email('Digite um e-mail válido')
              .required('Informe o email do funcionário para continuar.'),
            cargo: Yup.string().required(
              'Informe o cargo do funcionário para continuar',
            ),
            nome: Yup.string().required(
              'Infome o nome do funcionário para continuar',
            ),
            senha: Yup.string()
              .nullable()
              .notRequired()
              .when('senha', {
                is: (value) => value?.length,
                then: (rule: any) =>
                  rule.min(4, 'A senha precisa ter no mínimo 4 caracteres.'),
              }),
          },
          [['senha', 'senha']],
        );

        await schema.validate(data, { abortEarly: false });

        const { nome, cargo, email, tipo_acesso, senha } = data;

        api
          .put(`/usuario/funcionarios/${id}`, {
            email,
            cargo,
            nome,
            tipo_acesso,
            senha,
            acesso_pdv: acessoPdv,
          })
          .then(() => {
            addToast({
              type: 'success',
              title: 'Usuário Atualizado',
            });
            history.push('/configuracoes/4');
            setIsLoading(false);
          })
          .catch((err) => {
            if (
              err.response.status === 409 &&
              err?.response.data.message ===
                'Já existe um usuário cadastradado com esse login'
            ) {
              formRef.current?.setErrors({
                email: 'Email já possui cadastrado',
              });
            } else if (
              err.response.status === 403 ||
              err.response.status === 422
            ) {
              addToast({
                type: 'error',
                title: `${err.response.data.message}`,
              });
            } else {
              addToast({
                type: 'error',
                title: 'Erro no servidor',
              });
            }
            setIsLoading(false);
          });
      } catch (err) {
        console.log(err);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        } else {
          addToast({
            type: 'error',
            title: 'Erro durante a atualização',
            description: 'Ocorreu um erro ao atualizar o funcionário.',
          });
        }
        setIsLoading(false);
      }
    },
    [id, addToast, history, acessoPdv],
  );

  const handleDeleteFuncionario = useCallback(async () => {
    try {
      setIsLoading(true);
      api.delete(`/usuario/funcionarios/${id}`).then(() => {
        addToast({
          type: 'success',
          title: 'Usuário excluído com sucesso',
        });
        history.push('/configuracoes/4');
        setIsLoading(false);
        setModalDeleteIsOpen(false);
      });
    } catch (err) {
      console.log(err);
      addToast({
        type: 'error',
        title: 'Erro durante a exclusão.',
        description: 'Ocorreu um erro ao atualizar o funcionário.',
      });
      setIsLoading(false);
      setModalDeleteIsOpen(false);
    }
  }, [id, addToast, history]);

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/configuracoes/0');
    }

    async function loadFuncionario(): Promise<void> {
      const response = await api.get(`/usuario/${id}`);

      const { user: userRequest, acesso } = response.data;

      setFuncionario({
        nome: userRequest.nome,
        cargo: userRequest.cargo,
        email: userRequest.email,
        senha: '',
      });

      setAcessoPdv(userRequest.acesso_pdv !== false);

      setSelectedOption({
        id: acesso.id,
        value: acesso.tipo,
        label: acesso.nome,
      });
    }

    async function loadAcessos(): Promise<void> {
      const response = await api.get('/acesso');

      const optionItems: Option[] = [];

      response.data.map((acesso: any): void => {
        const optionFormat = {
          id: acesso.id,
          label: acesso.nome,
          value: acesso.role,
        };

        optionItems.push(optionFormat);
      });

      setOptions(optionItems);
    }

    loadAcessos();
    loadFuncionario();
    setIsLoading(false);
  }, [addToast, hasPermission, history, id, rotinas]);

  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/configuracoes/4')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Editar Funcionário</h1>
          <p>Preencha os dados abaixo para editar o funcionário.</p>
        </div>
      </header>

      <Divider />

      <S.CustomForm
        ref={formRef}
        onSubmit={handleEditFuncionario}
        initialData={{
          email: funcionario.email,
          nome: funcionario.nome,
          cargo: funcionario.cargo,
        }}
      >
        <div className="bloco-input multi">
          <Input
            name="nome"
            icon={HiOutlinePencil}
            label="Nome:"
            placeholder="Ex.: John Doe"
            disabled={isLoading}
          />
          <Input
            name="cargo"
            icon={MdWork}
            label="Cargo:"
            placeholder="Ex.: Gerente"
            disabled={isLoading}
          />
        </div>
        <div className="bloco-input multi">
          <Input
            name="email"
            icon={MdEmail}
            label="E-mail:"
            placeholder="Ex.: johndoe@example.com"
            disabled={isLoading}
          />
          <Input
            name="senha"
            icon={MdLock}
            type="password"
            label="Senha:"
            placeholder="Ex.: ********"
            disabled={isLoading}
          />
        </div>
        <div className="bloco-input">
          <label htmlFor="tipo_acesso">Tipo de Acesso:</label>
          <Select
            name="tipo_acesso"
            placeholder="Ex.: Administrador"
            options={options}
            value={selectedOption}
            onChange={(value: any) => setSelectedOption(value)}
            disabled={isLoading}
          />
        </div>
        <div className="bloco-input">
          <label htmlFor="acesso_pdv">Pode acessar o PDV</label>
          <Toggle
            firstText="Não"
            secondText="Sim"
            checked={acessoPdv}
            onToggle={setAcessoPdv}
          />
        </div>

        <div className="buttons">
          <Button full type="submit" disabled={isLoading}>
            Salvar
          </Button>
          <Button
            full
            type="button"
            variant="outlined"
            onClick={() => setModalDeleteIsOpen(true)}
            disabled={isLoading}
          >
            Excluir
          </Button>
        </div>
      </S.CustomForm>

      <Modal
        open={modalDeleteIsOpen}
        onClose={() => setModalDeleteIsOpen(false)}
        closeAfterTransition
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Slide direction="up" in={modalDeleteIsOpen} mountOnEnter unmountOnExit>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              width: 'fit-content',
              padding: '20px',
              height: 'fit-content',
              background: '#fff',
              borderRadius: '4px',
              position: 'relative',
              gap: '8px',
              maxWidth: '350px',
            }}
          >
            <S.ModalHeader>
              <div>
                <IoIosWarning />
              </div>
              <h1>Atenção</h1>
              <button type="button" onClick={() => setModalDeleteIsOpen(false)}>
                <BiX />
              </button>
            </S.ModalHeader>
            <p>
              Ao realizar a ação o funcionário &quot;{funcionario.nome}
              &quot; será excluídos.
              <br />
              <strong>Deseja prosseguir?</strong>
            </p>
            <Button full onClick={handleDeleteFuncionario} disabled={isLoading}>
              Confirmar
            </Button>
            <Button
              full
              variant="outlined"
              onClick={() => setModalDeleteIsOpen(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
          </Box>
        </Slide>
      </Modal>
    </S.Container>
  );
};

export default EditFuncionarioV2;
